/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Text } from '@fluentui/react/lib/Text';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { IntentsList } from 'src/luis/projects/analytics/components/OverallPredictionsCard/ProblemsSection/IntentsList';
import { InformationCard } from 'src/luis/projects/analytics/components/TrainingEvaluation/InformationCard';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { PassedFilters } from 'src/luis/projects/analytics/types/PassedFilters';
import { LuisId } from 'src/luis/types/Resource';

export type IncorrectProblemProps = {
    /**
     * The intents have the most large incorrect percentage.
     */
    incorrectIntents: IntentMetadata[];

    /**
     * A callback for when an intent is clicked.
     */
    onIntentClick: (intentId: LuisId, filters: PassedFilters) => void;
};

const messages = defineMessages({
    incorrectProblemTitle: {
        id: 'analytics.overallPredictionsCard.incorrectProblemTitle',
        defaultMessage: 'Incorrect predictions'
    },
    incorrectProblemDescription: {
        id: 'analytics.overallPredictionsCard.incorrectProblemDescription',
        defaultMessage: `These intents had the highest percentage of incorrect predictions.
            Consider revising the incorrectly predicted utterances in these intents.`
    }
});

const cardTheme = getScopedTheme('Dashboard');

/**
 * Represents the incorrect problem card.
 */
export const IncorrectProblem = (props: IncorrectProblemProps) => {
    const { incorrectIntents, onIntentClick } = props;
    const { localize } = useLocalization();

    return (
        <InformationCard title={localize(messages.incorrectProblemTitle)} iconName="SkypeCircleMinus" iconColor={cardTheme.incorrectColor}>
            <Text hidden={!incorrectIntents.length}>{localize(messages.incorrectProblemDescription)}</Text>
            <IntentsList
                intents={incorrectIntents.slice(0, 3)}
                onIntentClick={id => onIntentClick(id, { differenceFilters: ['incorrect'], nearestRivalIntentsFilters: [] })}
            />
        </InformationCard>
    );
};
